import React, { useEffect, useState } from 'react';
import { klona } from 'klona';
import { Icon } from '@fuxi/eevee-icon';
import { ShowTitle } from '../show-title';
import { setFormValue, setNextBtnConfig, useAIDiagnoseStore } from '../../store/useAIDiagnoseStore';
import { BodyDimensionSelector } from '../body-dimension-selector';
import { TPart } from '../select-part';

import cx from './index.module.scss';

interface Props {}

export const PART_2_LABEL_TEXT: Record<
  TPart,
  { name: string; partFields: AIDiagnose.DimensionType[]; extraFields?: AIDiagnose.DimensionType[] }
> = {
  waistCircumference: {
    name: '腰围',
    partFields: ['waistCircumference'],
  },
  backThickness: {
    name: '背厚',
    partFields: ['backThickness'],
  },
  armCircumference: {
    name: '臂围',
    partFields: ['leftArmCircumference', 'rightArmCircumference'],
  },
  thighCircumference: {
    name: '大腿围',
    partFields: ['leftThighCircumference', 'rightThighCircumference'],
    extraFields: ['leftCalfCircumference', 'rightCalfCircumference'],
  },
};

export const CurrentDimension: React.FC<Props> = () => {
  const { selectPart } = useAIDiagnoseStore.use.formValue();
  const [isPickerShow, setIsPickerShow] = useState(false);
  const { currentDimension } = useAIDiagnoseStore.use.formValue();
  const [currentPickerFields, setCurrentPickerFields] = useState<AIDiagnose.DimensionType[]>([]);

  useEffect(() => {
    // 应该填写的表单key值
    const shouldFillFieldsKey = selectPart
      ?.map(item => PART_2_LABEL_TEXT[item]?.partFields?.concat(PART_2_LABEL_TEXT[item]?.extraFields || []))
      .flat();

    setNextBtnConfig({ disable: shouldFillFieldsKey?.some(item => !currentDimension[item]) });
  }, [currentDimension, selectPart]);

  return (
    <div className={cx('CurrentDimension')}>
      <ShowTitle title="您的当前围度" subTitle="让我们更了解您的身体数据" />
      <div className={cx('dimension-form')}>
        {klona(selectPart)
          // 表单排序
          ?.sort((a, b) => Object.keys(PART_2_LABEL_TEXT)?.indexOf(a) - Object.keys(PART_2_LABEL_TEXT)?.indexOf(b))
          ?.map(item => (
            <div
              className={cx('part-form-item', {
                'no-value': !currentDimension[PART_2_LABEL_TEXT[item]?.partFields[0]],
              })}
              key={item}
              onClick={() => {
                setCurrentPickerFields(PART_2_LABEL_TEXT[item]?.partFields);
                setIsPickerShow(true);
              }}
            >
              <div className={cx('label')}>{PART_2_LABEL_TEXT[item]?.name}</div>
              <div className={cx('input-box')}>
                {['waistCircumference', 'backThickness'].includes(item) ? (
                  <div className={cx('input-item')}>
                    <span className={cx('value-text')}>
                      {currentDimension[item] ? (
                        <>
                          <span>{currentDimension[item]}</span>
                          <span className={cx('suffix')}>厘米</span>
                        </>
                      ) : (
                        <span>请录入{PART_2_LABEL_TEXT[item]?.name}</span>
                      )}
                    </span>
                    <Icon name="arrow-right" />
                  </div>
                ) : (
                  <div className={cx('input-item')}>
                    <div className={cx('left')}>
                      <span className={cx('value-text')}>
                        {currentDimension[PART_2_LABEL_TEXT[item]?.partFields[0]] ? (
                          <>
                            <span>{currentDimension[PART_2_LABEL_TEXT[item]?.partFields[0]]}</span>
                            <span className={cx('suffix')}>厘米</span>
                          </>
                        ) : (
                          <span>请录入左{PART_2_LABEL_TEXT[item]?.name}</span>
                        )}
                      </span>
                      <span className={cx('value-text')}>
                        {currentDimension[PART_2_LABEL_TEXT[item]?.partFields[1]] ? (
                          <>
                            <span>{currentDimension[PART_2_LABEL_TEXT[item]?.partFields[1]]}</span>
                            <span className={cx('suffix')}>厘米</span>
                          </>
                        ) : (
                          <span>请录入右{PART_2_LABEL_TEXT[item]?.name}</span>
                        )}
                      </span>
                    </div>
                    <Icon name="arrow-right" />
                  </div>
                )}
              </div>
            </div>
          ))}
        {selectPart?.includes('thighCircumference') && (
          <div
            className={cx('part-form-item', { 'no-value': !currentDimension['leftCalfCircumference'] })}
            onClick={() => {
              setCurrentPickerFields(['leftCalfCircumference', 'rightCalfCircumference']);
              setIsPickerShow(true);
            }}
          >
            <div className={cx('label')}>小腿围</div>
            <div className={cx('input-box')}>
              <div className={cx('input-item')}>
                <div className={cx('left')}>
                  <span className={cx('value-text')}>
                    {currentDimension['leftCalfCircumference'] ? (
                      <>
                        <span>{currentDimension['leftCalfCircumference']}</span>
                        <span className={cx('suffix')}>厘米</span>
                      </>
                    ) : (
                      <span>请录入左小腿围</span>
                    )}
                  </span>
                  <span className={cx('value-text')}>
                    {currentDimension['rightCalfCircumference'] ? (
                      <>
                        <span>{currentDimension['rightCalfCircumference']}</span>
                        <span className={cx('suffix')}>厘米</span>
                      </>
                    ) : (
                      <span>请录入右小腿围</span>
                    )}
                  </span>
                </div>
                <Icon name="arrow-right" />
              </div>
            </div>
          </div>
        )}
      </div>
      <BodyDimensionSelector
        value={currentDimension}
        onChange={value => {
          setFormValue(
            {
              currentDimension: value,
            },
            { isMerge: true }
          );
        }}
        isShow={isPickerShow}
        setIsShow={setIsPickerShow}
        fields={currentPickerFields}
        setFields={setCurrentPickerFields}
      />
    </div>
  );
};
